import { get, post, type RequestConfig } from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import {
  parseBhojanCreateOrderResponse,
  parseBhojanVerifyPaymentResponse,
  type BhojanCategory,
  type BhojanCreateOrderPayload,
  type BhojanCreateOrderResult,
  type BhojanPackage,
  type BhojanScheme,
  type BhojanVerifyPaymentPayload,
  type BhojanVerifyPaymentResult,
} from "@project/types/bhojan";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  isApiItemResponse,
  unwrapItem,
} from "../admin/list-api-helpers";

const CATEGORIES_BASE = `${JIVANDHARA_BASE}/bhojan-categories`;
const PACKAGES_BASE = `${JIVANDHARA_BASE}/bhojans`;
const SCHEMES_BASE = `${JIVANDHARA_BASE}/bhojan-schemes`;
const DONATIONS_BASE = `${JIVANDHARA_BASE}/bhojan-donations`;
const SILENT: RequestConfig = { showNotification: false };

class PublicBhojanService {
  listActiveCategories = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<BhojanCategory>> =>
    fetchListPage<BhojanCategory>(`${CATEGORIES_BASE}/`, {
      page_size: 100,
      ...query,
      status: "active",
    });

  listActivePackages = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<BhojanPackage>> =>
    fetchListPage<BhojanPackage>(`${PACKAGES_BASE}/`, {
      page_size: 100,
      ...query,
      status: "active",
    });

  listActiveSchemes = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<BhojanScheme>> =>
    fetchListPage<BhojanScheme>(`${SCHEMES_BASE}/`, {
      page_size: 100,
      ...query,
      status: "active",
    });

  getScheme = async (
    id: number | string
  ): Promise<BhojanScheme | undefined> => {
    const data = await get<ApiItemResponse<BhojanScheme> | BhojanScheme>(
      `${SCHEMES_BASE}/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  getPackage = async (
    id: number | string
  ): Promise<BhojanPackage | undefined> => {
    const data = await get<ApiItemResponse<BhojanPackage> | BhojanPackage>(
      `${PACKAGES_BASE}/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  createOrder = async (
    payload: BhojanCreateOrderPayload
  ): Promise<BhojanCreateOrderResult> => {
    const response = await post<unknown, BhojanCreateOrderPayload>(
      `${DONATIONS_BASE}/create-order/`,
      payload,
      SILENT
    );
    const parsed = parseBhojanCreateOrderResponse(
      unwrapItem(response) ?? response
    );
    if (!parsed.order_id) {
      throw new Error("Failed to create Bhojanshala payment order.");
    }
    return parsed;
  };

  verifyPayment = async (
    payload: BhojanVerifyPaymentPayload
  ): Promise<BhojanVerifyPaymentResult> => {
    const response = await post<unknown, BhojanVerifyPaymentPayload>(
      `${DONATIONS_BASE}/verify-payment/`,
      payload,
      SILENT
    );
    if (isApiItemResponse(response)) {
      return parseBhojanVerifyPaymentResponse(response.data ?? response);
    }
    return parseBhojanVerifyPaymentResponse(unwrapItem(response) ?? response);
  };
}

export const publicBhojanService = new PublicBhojanService();
